import { Injectable } from '@angular/core';
import {
  DuplicateLookupOption,
  findDuplicateProfileUpload,
  getActiveProfileUploadDocuments
} from '@app/lookup-user-detail/educator-duplicate-document.util';
import { environment } from '@env/environment';
import { EducatorDuplicateDocumentAuditService } from './educator-duplicate-document-audit.service';
import { EducatorDuplicateDocumentModalService } from './educator-duplicate-document-modal.service';

export interface EducatorDuplicateDocumentGuardParams {
  educatorDashboardRow: Record<string, unknown> | null | undefined;
  categoryKey: string;
  typeKey?: string;
  fileName: string;
  screenName: string;
  personId: number;
  categoryDisplay?: string;
  typeDisplay?: string;
  categoryLookups?: DuplicateLookupOption[];
  typeLookups?: DuplicateLookupOption[];
}

@Injectable({ providedIn: 'root' })
export class EducatorDuplicateDocumentGuardService {
  constructor(
    private readonly audit: EducatorDuplicateDocumentAuditService,
    private readonly duplicateModal: EducatorDuplicateDocumentModalService
  ) {}

  /**
   * Returns true when upload may proceed (no duplicate, or staff confirmed Proceed & Upload).
   * Returns false when duplicate detected and staff cancelled.
   */
  async ensureProceedOrCancel(params: EducatorDuplicateDocumentGuardParams): Promise<boolean> {
    const categoryKey = String(params.categoryKey ?? '').trim();
    const fileName = String(params.fileName ?? '').trim();
    if (!categoryKey || !fileName) {
      return true;
    }

    const activeDocs = getActiveProfileUploadDocuments(params.educatorDashboardRow);
    const duplicate = findDuplicateProfileUpload(activeDocs, {
      categoryKey,
      typeKey: params.typeKey,
      fileName,
      categoryLookups: params.categoryLookups,
      typeLookups: params.typeLookups
    });

    if ((environment as { enableDuplicateDocumentDebug?: boolean }).enableDuplicateDocumentDebug) {
      // eslint-disable-next-line no-console
      console.debug('[EducatorDuplicateDocumentGuard]', {
        screenName: params.screenName,
        activeDocCount: activeDocs.length,
        incoming: { categoryKey, typeKey: params.typeKey, fileName },
        sampleExisting: activeDocs.slice(0, 3).map((d) => ({
          cd_document_category: d['cd_document_category'],
          document_category: d['document_category'],
          cd_document_type: d['cd_document_type'],
          document_type: d['document_type'],
          file_name: d['file_name']
        })),
        duplicateFound: !!duplicate
      });
    }

    if (!duplicate) {
      return true;
    }

    this.audit.logDuplicateDetected({
      personId: params.personId,
      screenName: params.screenName,
      fileName,
      categoryKey,
      typeKey: params.typeKey,
      categoryDisplay: params.categoryDisplay,
      typeDisplay: params.typeDisplay
    });

    return this.duplicateModal.open();
  }
}
